"use client";
import isEmpty from "lodash/isEmpty";
import { useEffect, useMemo, useState } from "react";

import { useConfig } from "@stadium/hooks/lib/use-config";
import { useCheckoutContext } from "@stadium/checkout/src/contexts/checkout";

import Button from "@stadium/checkout/src/components/shared/buttons";
import Cards from "@stadium/checkout/src/components/payment-options/cards";
import Address from "@stadium/checkout/src/components/shipping-address/address";
import ReviewPaymentMethods from "@stadium/checkout/src/components/payment-review/payment-methods";
import Wallet from "@stadium/checkout/src/components/payment-options/wallet";
import PompAddOnSection from "@stadium/checkout/src/components/pomp-add-on-section/checkout-index";
import BillingDetails from "@stadium/checkout/src/components/billing-details";
import CheckoutShippingAddress from "@stadium/checkout/src/components/shipping-address";
import CorporateBilling from "@stadium/checkout/src/components/payment-options/corporate-billing";
import useWithAnalytics from "@stadium/services/analytics/hooks/use-with-analytics";
import {
  ANALYTICS_EVENT_ACTION_VIEWED,
  ANALYTICS_EVENT_ACTION_EDITED,
  ANALYTICS_EVENT_ACTION_SAVED,
  ANALYTICS_EVENT_ACTION_EXPANDED,
  ANALYTICS_EVENT_ACTION_COLLAPSED,
} from "@stadium/services/analytics/constants/default";
import {
  SECTION_TARGET,
  BILLING_INFO_TARGET,
  PAYMENT_METHOD_TARGET,
} from "@stadium/checkout/src/constants/analytics";

import {
  getCheckoutStepsInitState,
  getNextCheckoutStep,
  getOrderDefaultCompletedSteps,
} from "@stadium/checkout/src/helpers/checkout";
import {
  getAllCountries,
  getPompFeeInUsd,
  getWarehouseAddressId,
} from "@stadium/redux-utils/selectors/configSelector";

import {
  PAYMENT_SELECTION_STEP_STATE,
  POMP_STEP_STATE,
  SELECT_ADDRESS_STEP_STATE,
  DEFAULT_CHECKOUT_STEPS,
  BILLING_DETAILS_STEP_STATE,
} from "@stadium/checkout/src/constants/order";
import { DEFAULT_CURRENCY_CODE } from "@stadium/constants";
import { PAYMENT_METHODS } from "@stadium/checkout/src/constants/payment";
import { useFlags } from "@stadium/services/launch-darkly";
import { threePlWarehouseUpdatesFlag } from "@stadium/services/launch-darkly/constants";

import styles from "./main.module.scss";

const { corporateBilled } = PAYMENT_METHODS;

const emptyArr = [];
const emptyObj = {};

const SECTION_NAME_MAPPING = {
  [SELECT_ADDRESS_STEP_STATE]: "address",
  [POMP_STEP_STATE]: "protection",
  [PAYMENT_SELECTION_STEP_STATE]: "payment",
  [BILLING_DETAILS_STEP_STATE]: "billing",
};

const CheckoutStepsMain = ({
  teamWallets = emptyArr,
  storeDetails = emptyObj,
  orderTotal,
  orderTotalForInsufficientFundsCheck,
  savedCreditCards = emptyArr,
  refetchCreditCards,
  selectedCountry = emptyObj,
  showWarehouseAddress,
  isDefaultAddressWarehouse,
  selectedAddress = emptyObj,
  setSelectedAddress,
  payableAmount,
  checkoutSteps = DEFAULT_CHECKOUT_STEPS,
  optionalSteps = [],
  canHaveOptionalSteps = false,
  isDisabled = false,
  renderCustomContinueCta,
  isExistingOrder = false,
  withCorporateBilling = false,
  isCreditCardsLoading = false,
  billingDetailsAdditionalProps = {},
  isSingleSourcePayment = false,
  showStadiumPoints = true,
  hideCountryFlag = false,
  isCreditCardsDefaultCollapsed = false,
  warehouseDetails = null,
}) => {
  const {
    updateCheckoutStepsState,
    checkoutStepsState,
    currency,
    currentCheckoutStep,
    setCurrentCheckoutStep,
    setCheckoutStepsState,
    selectedTeamWallet,
    creditCardId,
    billingType,
    orderData,
    setOrderData,
    initOrderData,
    postOrderUpdateCallback,
    updateOrder,
    isUpdatingOrder,
    useStadiumPoints,
    useWallet,
    submittedAddress,
    setSubmittedAddress,
    setPompSelected,
    setBillingCountry,
    setBillingZipCode,
    setBillingNote,
    setBillingTaxNumber,
  } = useCheckoutContext();

  const withAnalytics = useWithAnalytics();

  useEffect(() => {
    if (currentCheckoutStep) {
      withAnalytics(undefined, {
        track: true,
        target: SECTION_TARGET,
        action: ANALYTICS_EVENT_ACTION_VIEWED,
        eventProperties: {
          section_name: SECTION_NAME_MAPPING[currentCheckoutStep],
        },
      })();
    }
  }, [currentCheckoutStep]);

  const countries = useConfig(getAllCountries);
  const warehouseAddressId = useConfig(getWarehouseAddressId);

  const { [threePlWarehouseUpdatesFlag]: isThreePlWarehouseUpdatesEnabled } = useFlags();

  const isOrderWarehouseAddress = useMemo(() => {
    const useWarehouseIdForOrderUpdate =
      isThreePlWarehouseUpdatesEnabled && !warehouseDetails?.defaultWarehouse;

    const resolvedWarehouseAddressId = useWarehouseIdForOrderUpdate
      ? warehouseDetails?.externalId
      : warehouseAddressId;

    return useWarehouseIdForOrderUpdate
      ? Boolean(
          resolvedWarehouseAddressId && initOrderData?.warehouseId === resolvedWarehouseAddressId,
        )
      : initOrderData?.shipAddressId === warehouseAddressId;
  }, [
    initOrderData?.warehouseId,
    initOrderData?.shipAddressId,
    isThreePlWarehouseUpdatesEnabled,
    warehouseDetails,
    warehouseAddressId,
  ]);

  const isLastStep = useMemo(() => {
    return currentCheckoutStep === checkoutSteps[checkoutSteps.length - 1];
  }, [checkoutSteps, currentCheckoutStep]);

  const [isCardsSectionOpen, setIsCardsSectionOpen] = useState(!isCreditCardsDefaultCollapsed);

  const toggleCardsSection = () => {
    withAnalytics(setIsCardsSectionOpen, {
      track: true,
      target: PAYMENT_METHOD_TARGET,
      action: isCardsSectionOpen
        ? ANALYTICS_EVENT_ACTION_COLLAPSED
        : ANALYTICS_EVENT_ACTION_EXPANDED,
    })((prev) => !prev);
  };

  useEffect(() => {
    setIsCardsSectionOpen(!isCreditCardsDefaultCollapsed);
  }, [isCreditCardsDefaultCollapsed]);

  useEffect(() => {
    if (
      !isEmpty(checkoutSteps) &&
      !isEmpty(initOrderData) &&
      (isEmpty(checkoutStepsState) || canHaveOptionalSteps)
    ) {
      const defaultCompletedSteps =
        checkoutSteps.length > 1 && !isDisabled
          ? getOrderDefaultCompletedSteps(initOrderData, checkoutStepsState)
          : [];

      setCheckoutStepsState(
        getCheckoutStepsInitState({ checkoutSteps, defaultCompletedSteps, optionalSteps }),
      );
      !isDisabled &&
        !currentCheckoutStep &&
        setCurrentCheckoutStep(checkoutSteps.find((step) => !defaultCompletedSteps.includes(step)));
    }
  }, [checkoutSteps, optionalSteps, isDisabled]);

  const pompFeeInUsd = useConfig(getPompFeeInUsd);

  const selectedCreditCard = useMemo(() => {
    if (!creditCardId) return emptyObj;

    return savedCreditCards?.find(({ id }) => +id === +creditCardId);
  }, [creditCardId, savedCreditCards, isCreditCardsLoading]);

  const isCorporateBilled = billingType === corporateBilled.key;

  const resetUnsavedChanges = (key) => {
    if (key === SELECT_ADDRESS_STEP_STATE && submittedAddress) {
      setSelectedAddress(submittedAddress);
    }

    if (key === POMP_STEP_STATE) {
      setPompSelected(orderData.hasPomp);
    }

    if (key === BILLING_DETAILS_STEP_STATE) {
      setBillingCountry(
        countries.find((country) => country.id === orderData.billingDetailAttributes?.countryId),
      );

      setBillingZipCode(orderData.billingDetailAttributes?.zipcode);
      setBillingNote(orderData.billingCode);
      setBillingTaxNumber(orderData.taxNumber);
    }
  };

  const handleEditCheckoutStep = (key, shouldResetUnsavedChanges = true) => {
    checkoutStepsState[currentCheckoutStep]?.submitted &&
      shouldResetUnsavedChanges &&
      resetUnsavedChanges(currentCheckoutStep);

    withAnalytics(undefined, {
      track: true,
      target: SECTION_TARGET,
      action: ANALYTICS_EVENT_ACTION_EDITED,
      eventProperties: {
        section_name: SECTION_NAME_MAPPING[key],
      },
    })();

    updateCheckoutStepsState(key);
    setCurrentCheckoutStep(key);
  };

  const handleContinueToNextStep = () => {
    updateCheckoutStepsState(currentCheckoutStep);
    setCurrentCheckoutStep(getNextCheckoutStep(currentCheckoutStep, checkoutStepsState));
  };

  const handleUpdateOrder = (payload, stepToAutoEdit = "") => {
    if (isEmpty(payload)) {
      handleContinueToNextStep();
      return;
    }

    const updateOrderCallback = (order) => {
      const shouldAutoEditStep =
        stepToAutoEdit &&
        checkoutStepsState[currentCheckoutStep]?.submitted &&
        checkoutStepsState[stepToAutoEdit]?.submitted;

      if (currentCheckoutStep === BILLING_DETAILS_STEP_STATE) {
        withAnalytics(undefined, {
          track: true,
          target: BILLING_INFO_TARGET,
          action: ANALYTICS_EVENT_ACTION_SAVED,
        })();
      }

      setOrderData((prev) => ({ ...prev, ...payload }));
      handleContinueToNextStep();
      postOrderUpdateCallback(order);

      if (!isEmpty(selectedAddress)) {
        setSubmittedAddress(selectedAddress);
      }

      if (shouldAutoEditStep) {
        handleEditCheckoutStep(stepToAutoEdit, false);
      }
    };

    updateOrder(payload, updateOrderCallback);
  };

  const renderContinueCta = ({
    isDisabled = false,
    payload = {},
    ctaText = "Continue",
    stepToAutoEdit = "",
  } = {}) => {
    if (renderCustomContinueCta && isLastStep) return renderCustomContinueCta({ isDisabled });

    return (
      <div className={styles.continueActionBlock}>
        <Button
          disabled={isDisabled}
          onClick={() => handleUpdateOrder(payload, stepToAutoEdit)}
          isLoading={!isEmpty(payload) && isUpdatingOrder}
          className={styles.continueCta}
        >
          {checkoutStepsState[currentCheckoutStep]?.submitted &&
          currentCheckoutStep !== PAYMENT_SELECTION_STEP_STATE
            ? "Update"
            : ctaText}
        </Button>
      </div>
    );
  };

  const renderSavedCardsHeader = () => {
    if (savedCreditCards.length === 0) return null;

    if (!isCreditCardsDefaultCollapsed)
      return <div className={styles.saveCardsTitle}>SAVED CARDS</div>;

    return (
      <Button
        styleType="link"
        className={`${styles.addCardButton} ${
          isCardsSectionOpen ? styles.addCardSelected : styles.collapseCard
        }`}
        onClick={toggleCardsSection}
      >
        <span className={`${isCardsSectionOpen ? styles.subtractCardIcon : styles.addCardIcon} `} />
        SAVED CARDS
      </Button>
    );
  };

  const renderPaymentMethods = (checkoutStep) => {
    const isPaymentMethodSelected = Boolean(
      useWallet || selectedTeamWallet?.id || creditCardId || isCorporateBilled || useStadiumPoints,
    );

    const isContinueDisabled = isSingleSourcePayment
      ? !isPaymentMethodSelected
      : payableAmount && !creditCardId && !isCorporateBilled;

    if (currentCheckoutStep === checkoutStep.key)
      return (
        <div className={`${styles.detailBlock} ${styles.paymentDetailBlock}`}>
          <div className={styles.headingBlock}>
            <div className={styles.headingContent}>
              <i className={`icon ${styles.paymentIcon}`} /> Payment
            </div>
          </div>
          <Wallet
            disableCollapse
            orderTotal={orderTotal}
            orderTotalForInsufficientFundsCheck={orderTotalForInsufficientFundsCheck}
            billingCurrencyCode={currency?.code}
            teamWallets={teamWallets}
            storeDetails={storeDetails}
            isExistingOrder={isExistingOrder}
            isSingleSourcePayment={isSingleSourcePayment}
            showStadiumPoints={showStadiumPoints}
            isTeamWalletsLoading={isCreditCardsLoading}
            eventLocationAnalytics={{
              track: true,
              target: PAYMENT_METHOD_TARGET,
              action: ANALYTICS_EVENT_ACTION_EDITED,
            }}
          />
          <div className={styles.divider} />

          {renderSavedCardsHeader()}

          {isCardsSectionOpen || isEmpty(savedCreditCards) ? (
            <Cards
              creditCards={savedCreditCards}
              refetchCreditCards={refetchCreditCards}
              isExistingOrder={isExistingOrder}
              addCreditCardSectionClassName={styles.creditCardSection}
              isCreditCardsLoading={isCreditCardsLoading}
              defaultValues={{ openNewCardFormByDefault: !isCreditCardsDefaultCollapsed }}
            />
          ) : null}

          {withCorporateBilling ? (
            <>
              <div className={`${styles.divider} ${styles.withTopMargin}`} />
              <CorporateBilling
                orderTotal={payableAmount || orderTotal}
                isExistingOrder={isExistingOrder}
                isDefaultSelected={isCorporateBilled}
                className={styles.corporateBlock}
              />
            </>
          ) : null}

          {renderContinueCta({
            isDisabled: isContinueDisabled,
          })}
        </div>
      );

    if (checkoutStep.submitted)
      return (
        <ReviewPaymentMethods
          teamWallet={selectedTeamWallet}
          creditCard={selectedCreditCard}
          orderTotal={orderTotal}
          onEdit={() => handleEditCheckoutStep(checkoutStep.key)}
          showBillingSection={false}
          storeDetails={storeDetails}
          isSingleSourcePayment={isSingleSourcePayment}
        />
      );
    return (
      <div className={styles.headingBlock}>
        <div className={styles.headingContent}>
          <i className={`icon ${styles.paymentIcon}`} /> Payment
        </div>
      </div>
    );
  };

  const renderAddressSection = (checkoutStep) => {
    if (isEmpty(orderData)) return null;

    const isOpen = currentCheckoutStep === checkoutStep.key;

    const addressProps = {
      selectedCountry,
      showWarehouseAddress,
      isDefaultAddressWarehouse,
      selectedAddress,
      setSelectedAddress,
      setSubmittedAddress,
      renderContinueCta,
      orderData,
      defaultAddressId: isOrderWarehouseAddress ? null : initOrderData?.shipAddressStadiumId,
      warehouseDetails,
    };

    if (isOpen || (isEmpty(selectedAddress) && !isDisabled))
      return <CheckoutShippingAddress {...addressProps} hideCountryFlag={hideCountryFlag} />;

    if (checkoutStep.submitted)
      return (
        <div className={`${styles.detailBlock} ${styles.addressBlock}`}>
          <div className={styles.headingBlock}>
            <div className={styles.headingContent}>
              <i className={styles.shipIcon} /> Shipping
            </div>
          </div>

          <Address
            address={selectedAddress}
            onEditAddress={() => handleEditCheckoutStep(checkoutStep.key)}
            className={styles.addressDetail}
            editClassName={styles.editAddress}
          />
        </div>
      );

    return (
      <div className={`${styles.headingBlock} ${styles.withBtmMargin}`}>
        <div className={styles.headingContent}>
          <i className={styles.shipIcon} /> Shipping
        </div>
      </div>
    );
  };

  const renderCheckoutStep = (checkoutStep) => {
    switch (checkoutStep.key) {
      case SELECT_ADDRESS_STEP_STATE:
        return renderAddressSection(checkoutStep);
      case POMP_STEP_STATE:
        return (
          <PompAddOnSection
            pompFee={`$${pompFeeInUsd} ${DEFAULT_CURRENCY_CODE}`}
            renderContinueCta={renderContinueCta}
            handleEditPomp={handleEditCheckoutStep}
          />
        );
      case PAYMENT_SELECTION_STEP_STATE:
        return renderPaymentMethods(checkoutStep);
      case BILLING_DETAILS_STEP_STATE:
        return (
          <BillingDetails
            isExistingOrder={isExistingOrder}
            renderContinueCta={renderContinueCta}
            handleEditBillingDetails={handleEditCheckoutStep}
            {...billingDetailsAdditionalProps}
          />
        );
      default:
        return null;
    }
  };

  const renderCheckoutSteps = () => Object.values(checkoutStepsState).map(renderCheckoutStep);

  return <div>{renderCheckoutSteps()}</div>;
};

export default CheckoutStepsMain;
